import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { finalize } from 'rxjs';
import { assertSuccessEnvelope } from 'src/app/core/http/api-envelope.operators';
import { resolveApiErrorMessage } from 'src/app/core/http/api-http-error';
import { CountryPackRecord, RulesApiService } from 'src/app/core/services/rules-api.service';
import { DeleteConfirmModalComponent } from 'src/app/shared/components/delete-confirm-modal/delete-confirm-modal.component';
import { EntityCatalogComponent } from 'src/app/shared/components/entity-catalog/entity-catalog.component';
import { entityText } from 'src/app/shared/components/entity-catalog/entity-text.util';
import { EntityCatalog, EntityRecord } from 'src/app/shared/components/entity-catalog/entity-catalog.types';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';
import { RestoreConfirmModalComponent } from 'src/app/shared/components/restore-confirm-modal/restore-confirm-modal.component';
import { COUNTRY_PACK_CATALOG } from './country-packs.catalog';

type CountryPackView = 'active' | 'deleted';

const DELETED_CATALOG: EntityCatalog = {
  ...COUNTRY_PACK_CATALOG,
  fields: COUNTRY_PACK_CATALOG.fields.map((field) =>
    field.key === 'deleted_at' ? { ...field, inList: true } : { ...field, inList: !!field.inList && field.key !== 'status' },
  ).concat(
    COUNTRY_PACK_CATALOG.fields.some((f) => f.key === 'deleted_at')
      ? []
      : [{ key: 'deleted_at', label: 'Deleted at', kind: 'text' as const, inList: true }],
  ),
};

/**
 * Country pack master screen — step 1 of onboarding a country without a deploy.
 *
 * The screen itself is declared in COUNTRY_PACK_CATALOG; this component only loads and persists.
 */
@Component({
  selector: 'app-country-packs',
  standalone: true,
  imports: [
    CommonModule,
    EntityCatalogComponent,
    PageSurfaceHeaderComponent,
    DeleteConfirmModalComponent,
    RestoreConfirmModalComponent,
  ],
  templateUrl: './country-packs.component.html',
})
export class CountryPacksComponent implements OnInit {
  private readonly api = inject(RulesApiService);

  readonly activeCatalog = COUNTRY_PACK_CATALOG;
  readonly deletedCatalog = DELETED_CATALOG;
  readonly toolbarTabs = [{ key: 'deleted', label: 'Deleted' }];

  view: CountryPackView = 'active';
  records: EntityRecord[] = [];
  loading = false;
  saving = false;
  error = '';

  showDeleteModal = false;
  deleteError = '';
  readonly deleteLoading = signal(false);
  selectedForDelete: EntityRecord | null = null;

  showRestoreModal = false;
  restoreError = '';
  readonly restoreLoading = signal(false);
  selectedForRestore: EntityRecord | null = null;

  ngOnInit(): void {
    this.load();
  }

  get catalog(): EntityCatalog {
    return this.view === 'deleted' ? this.deletedCatalog : this.activeCatalog;
  }

  get activeToolbarTab(): string {
    return this.view === 'deleted' ? 'deleted' : '';
  }

  onToolbarTabChange(key: string): void {
    this.view = this.view === key ? 'active' : (key as CountryPackView);
    this.error = '';
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = '';
    const request$ =
      this.view === 'deleted'
        ? this.api.listDeletedCountryPackRecords()
        : this.api.listCountryPackRecords();

    request$
      .pipe(
        assertSuccessEnvelope(),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (res) => {
          const rows = (res.data ?? []) as unknown as EntityRecord[];
          this.records =
            this.view === 'deleted'
              ? rows.map((row) => ({
                  ...row,
                  deleted_at: this.formatDeletedAt(row['deleted_at']),
                }))
              : rows;
        },
        error: (err) => (this.error = resolveApiErrorMessage(err)),
      });
  }

  onSave(event: { record: EntityRecord; isNew: boolean }): void {
    const body = this.toRequestBody(event.record);
    const request$ = event.isNew
      ? this.api.createCountryPackRecord(body)
      : this.api.updateCountryPackRecord(String(event.record['code']), body);

    this.saving = true;
    this.error = '';
    request$
      .pipe(
        assertSuccessEnvelope(),
        finalize(() => (this.saving = false)),
      )
      .subscribe({
        next: () => this.load(),
        error: (err) => (this.error = resolveApiErrorMessage(err)),
      });
  }

  onDelete(row: EntityRecord): void {
    this.deleteError = '';
    this.selectedForDelete = row;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.selectedForDelete = null;
    this.deleteError = '';
  }

  submitDelete(): void {
    const row = this.selectedForDelete;
    if (!row) return;

    const code = entityText(row['code']).trim();
    if (!code) return;

    this.deleteLoading.set(true);
    this.deleteError = '';
    this.api
      .deleteCountryPackRecord(code)
      .pipe(
        assertSuccessEnvelope(),
        finalize(() => this.deleteLoading.set(false)),
      )
      .subscribe({
        next: () => {
          this.closeDeleteModal();
          this.load();
        },
        error: (err) => (this.deleteError = resolveApiErrorMessage(err)),
      });
  }

  onRestore(row: EntityRecord): void {
    this.restoreError = '';
    this.selectedForRestore = row;
    this.showRestoreModal = true;
  }

  closeRestoreModal(): void {
    this.showRestoreModal = false;
    this.selectedForRestore = null;
    this.restoreError = '';
  }

  submitRestore(): void {
    const row = this.selectedForRestore;
    if (!row) return;

    const code = entityText(row['code']).trim();
    if (!code) return;

    this.restoreLoading.set(true);
    this.restoreError = '';
    this.api
      .restoreCountryPackRecord(code)
      .pipe(
        assertSuccessEnvelope(),
        finalize(() => this.restoreLoading.set(false)),
      )
      .subscribe({
        next: () => {
          this.closeRestoreModal();
          this.view = 'active';
          this.load();
        },
        error: (err) => {
          this.restoreError = resolveApiErrorMessage(err);
          this.error = this.restoreError;
        },
      });
  }

  get deleteEntityName(): string {
    const row = this.selectedForDelete;
    if (!row) return '';
    const name = entityText(row['display_name']).trim();
    const code = entityText(row['code']).trim();
    return name || code;
  }

  get restoreEntityName(): string {
    const row = this.selectedForRestore;
    if (!row) return '';
    const name = entityText(row['display_name']).trim();
    const code = entityText(row['code']).trim();
    return name ? `${code} — ${name}` : code;
  }

  private formatDeletedAt(value: unknown): string {
    if (value == null || value === '') return '—';
    const text = entityText(value);
    const date = new Date(text);
    if (Number.isNaN(date.getTime())) return text || '—';
    return date.toLocaleString();
  }

  private toRequestBody(record: EntityRecord): Partial<CountryPackRecord> {
    const body: Record<string, unknown> = {};
    for (const field of this.activeCatalog.fields) {
      const value = record[field.key];
      if (value === '' || value == null) continue;
      body[field.key] =
        field.key === 'code' && typeof value === 'string' ? value.trim().toUpperCase() : value;
    }
    return body as Partial<CountryPackRecord>;
  }
}
